/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { BulbOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useEffect, useState } from 'react'
import Button from 'src/components/ts-components/Button'
import BaseModal from 'src/components/ts-components/BaseModal'
import ScrollContainer from 'src/components/ts-components/ScrollContainer'
import Text from 'src/components/ts-components/Text'
import { collectTimezoneCheck } from './collectTimezoneCheck'

type TimezoneCheckProps = {
  isOpen: boolean
  onClose: () => void
}

const TimezoneCheck = ({ isOpen, onClose }: TimezoneCheckProps) => {
  const [report, setReport] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setReport(null)
      setCopied(false)
    }
  }, [isOpen])

  const runCheck = useCallback(() => {
    setCopied(false)
    setReport(collectTimezoneCheck())
  }, [])

  const copyReport = useCallback(() => {
    if (!report) {
      return
    }
    navigator.clipboard
      ?.writeText(report)
      .then(() => {
        setCopied(true)
      })
      .catch(() => {
        // Clipboard can be blocked (permission, insecure context). The report
        // stays on screen so it can still be selected.
      })
  }, [report])

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title='Timezone Check'
      desktopWidth='560px'
      actions={
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 16px;
            width: 50%;
            margin-left: auto;
            @media (max-width: 768px) {
              width: 100%;
            }
          `}
        >
          <Button text='Close' onClick={onClose} variant='secondary' />
          <Button text='Run check' onClick={runCheck} variant='primary' />
        </div>
      }
    >
      <ScrollContainer css={scrollAreaStyle}>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <Text variant='paragraphMedium' color={colors.WHITE}>
            This reads the timezone your browser is using to display game and
            event times.
          </Text>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 4px;
              padding: 12px;
              border-radius: 8px;
              background: ${colors.NEUTRAL_800};
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <BulbOutlined
                css={css`
                  color: ${colors.PRIMARY};
                  font-size: 16px;
                `}
              />
              <Text
                variant='paragraphSmall'
                weight='semiBold'
                color={colors.WHITE}
              >
                Hint
              </Text>
            </div>
            <Text variant='paragraphSmall' color={colors.NEUTRAL_50}>
              If a game or event time looks off, switch your computer&apos;s
              timezone to a different zone and then back to yours, close the
              browser and open it again, then run the check. Copy the result and
              send it to us so we can follow up.
            </Text>
          </div>
          <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
            Open this again from the home screen with Ctrl+Shift+Y on Windows,
            or Command+Shift+Y on Mac.
          </Text>
          {report ? (
            <>
              <pre
                css={css`
                  margin: 0;
                  padding: 12px;
                  border-radius: 8px;
                  background: ${colors.NEUTRAL_800};
                  color: ${colors.NEUTRAL_0};
                  font-size: 12px;
                  line-height: 1.5;
                  white-space: pre-wrap;
                  word-break: break-word;
                `}
              >
                {report}
              </pre>
              <div
                css={css`
                  width: 180px;
                `}
              >
                <Button
                  text={copied ? 'Copied' : 'Copy result'}
                  onClick={copyReport}
                  variant='secondary'
                />
              </div>
            </>
          ) : null}
        </div>
      </ScrollContainer>
    </BaseModal>
  )
}

/*
 * BaseModal's desktop height is `auto` and its body does not scroll, so on a
 * short window the dialog grows past the screen and the footer is cut off.
 * Capping the scroll area keeps the title and the buttons on screen. The
 * number leaves room for the header, the footer, and the dialog's own margin.
 */
const scrollAreaStyle = css`
  max-height: calc(100vh - 260px);
`

export default TimezoneCheck
